'use client'

import React, { useCallback, useMemo, useState } from 'react'
import { useLocale, useTranslations } from 'next-intl'

import type { ContactSupportData } from '@/src/constants/contactSupport'

import { ContactFormView, type ContactFormFieldValues } from './contactForm'

const initialForm = (): ContactFormFieldValues => ({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    stdCode: '+966',
    subject: '',
    message: ''
})

const ContactFormWrapper: React.FC<{ supportData: ContactSupportData }> = ({ supportData }) => {
    const t = useTranslations('contact')
    const locale = useLocale()
    const isRtl = locale === 'ar'

    const [formData, setFormData] = useState<ContactFormFieldValues>(initialForm)
    const [loading, setLoading] = useState(false)
    const [status, setStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null)

    const handleReset = useCallback(() => {
        setFormData(initialForm())
    }, [])

    const onFieldChange = useCallback((field: keyof ContactFormFieldValues, value: string) => {
        setFormData(prev => ({ ...prev, [field]: value }))
    }, [])

    const handleSubmit = useCallback(
        async (e: React.FormEvent) => {
            e.preventDefault()
            setLoading(true)
            setStatus(null)

            try {
                const params = new URLSearchParams()
                Object.entries(formData).forEach(([key, value]) => {
                    params.append(`data[${key}]`, value)
                })

                const response = await fetch('https://www.flyin.com/sendFeedbackMail', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
                        Accept: '*/*'
                    },
                    body: params.toString()
                })

                if (response.ok) {
                    setStatus({ type: 'success', message: t('successMessage') })
                    handleReset()
                } else {
                    setStatus({ type: 'error', message: t('errorSendFailed') })
                }
            } catch {
                setStatus({ type: 'error', message: t('errorGeneric') })
            } finally {
                setLoading(false)
            }
        },
        [formData, handleReset, t]
    )

    const copy = useMemo(
        () => ({
            title: t('title'),
            subtitle: t('subtitle'),
            firstName: t('firstName'),
            lastName: t('lastName'),
            emailId: t('emailId'),
            phoneNumber: t('phoneNumber'),
            subject: t('subject'),
            message: t('message'),
            selectPlaceholder: t('selectPlaceholder'),
            reset: t('reset'),
            submit: t('submit'),
            customerServiceLine1: t('customerServiceLine1'),
            customerServiceLine2: t('customerServiceLine2'),
            callsFrom:
                supportData.callsFromLine ??
                t('callsFrom', { region: supportData.callsFromRegion ?? supportData.address }),
            internationalWhatsapp: t('internationalWhatsapp'),
            generalInquiries: t('generalInquiries'),
            officeAddressTitle: t('officeAddressTitle'),
            contactNumber: t('contactNumber'),
            registeredNumber: t('registeredNumber'),
            vat: supportData.vatLineKind === 'taxCard' ? t('taxCard') : t('vat')
        }),
        [supportData.address, supportData.callsFromLine, supportData.callsFromRegion, supportData.vatLineKind, t]
    )

    const subjectOptions = useMemo(
        () => [
            { value: 'General Inquiry', label: t('subjectGeneralInquiry') },
            { value: 'Technical Support', label: t('subjectTechnicalSupport') },
            { value: 'Sales', label: t('subjectSales') },
            { value: 'Billing', label: t('subjectBilling') },
            { value: 'Feedback', label: t('subjectFeedback') }
        ],
        [t]
    )

    const stdCodeOptions = useMemo(
        () => [
            { value: '+966', label: '+966' },
            { value: '+1', label: '+1' },
            { value: '+44', label: '+44' },
            { value: '+91', label: '+91' },
            { value: '+971', label: '+971' }
        ],
        []
    )

    return (
        <ContactFormView
            supportData={supportData}
            formData={formData}
            onFieldChange={onFieldChange}
            onSubmit={handleSubmit}
            onReset={handleReset}
            loading={loading}
            status={status}
            onDismissStatus={() => setStatus(null)}
            isRtl={isRtl}
            copy={copy}
            subjectOptions={subjectOptions}
            stdCodeOptions={stdCodeOptions}
        />
    )
}

export { ContactFormWrapper }
export default ContactFormWrapper
